import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { IWatermarking } from "reducers/watermarking";
import { useWatermarking } from "hooks/useWatermarking";
import { processSingleImage } from "helpers/watermarking";
import { addQueryParam } from "helpers/query-helpers";
import { useGetSafeImages } from "helpers/product-media-helpers";
import type { IScope } from "reducers/scope";

interface IProps {
    children: (src: string, isLoading?: boolean) => React.ReactNode | Element;
    // disables internal loader, transfer loading state to children
    imageUrl: string;
    active?: boolean;
}

interface IStateProps {
    scope: IScope;
    watermarking: IWatermarking;
}

const WatermarkProvider = (props: IProps & IStateProps) => {
    const { children, active, imageUrl, scope, watermarking } = props;
    const { brand, company } = scope || {};
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    // indicates if image already was processed
    const [isCalled, setIsCalled] = useState<boolean>(false);
    const [src, setSrc] = useState<string>(null);

    const useWatermarks = useMemo(
        () => brand?.hasPreference("add_watermarking") && watermarking?.active,
        [brand, watermarking?.active],
    );
    const getSafeImages = useGetSafeImages(scope);
    const isFallbackImage = useMemo(() => getSafeImages(null).includes(imageUrl), [getSafeImages, imageUrl]);
    // add query parameter to avoid using cached GET request, get rid of CORS errors
    const url = useMemo(
        () =>
            useWatermarks && !isFallbackImage ? imageUrl && addQueryParam(imageUrl, { unique_url: "true" }) : imageUrl,
        [useWatermarks, isFallbackImage, imageUrl],
    );

    const svgBase64 = useWatermarking({
        onIsFetchingChange: setIsProcessing,
        brand,
        company,
        watermarking,
    });

    // clean called state to display new image if passed
    useEffect(() => {
        setIsCalled(false);
    }, [url]);

    useEffect(() => {
        if (active !== false && !isCalled) {
            setIsCalled(true);

            if (isFallbackImage || !svgBase64) {
                setSrc(url);
            } else if (url) {
                setIsProcessing?.(true);
                processSingleImage(url, svgBase64).then((image) => {
                    setSrc(image);
                    setIsProcessing?.(false);
                });
            }
        }
    }, [svgBase64, active, isCalled, url, isFallbackImage]);

    return children(src, isProcessing);
};

const mapStateToProps = (store: IStore): IStateProps => ({
    scope: store.scope,
    watermarking: store.watermarking.payload,
});

export default connect<IStateProps, null, IProps>(mapStateToProps)(WatermarkProvider as any);
